import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { useTheme } from '../context/ThemeContext';
import ThemeToggle from './ThemeToggle';
import { useAssistant } from './support/AssistantWorkspace';
import {
  LayoutDashboard,
  LifeBuoy,
  Bot,
  Building2,
  Users,
  Calendar,
  MessageSquare,
  Plug,
  LogOut,
  Menu,
  X,
  ChevronRight,
  CreditCard,
  Brain,
  UserCog,
  Star,
  Facebook,
  Music,
  Search,
  Send,
  Settings,
  Instagram,
  Flame,
  Activity,
  Mail,
  Kanban,
  BarChart3
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const menuItems = [
  { path: '/onboarding', icon: LifeBuoy, label: 'Configuración inicial' },
  { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/dashboard/clinic', icon: Building2, label: 'Mi Clínica' },
  { path: '/dashboard/training', icon: Brain, label: 'Entrenamiento IA' },
  { path: '/dashboard/ai', icon: Bot, label: 'Gina y horarios' },
  { path: '/dashboard/leads', icon: Users, label: 'Leads' },
  { path: '/dashboard/hot-leads', icon: Flame, label: 'Hot Leads 🔥' },
  { path: '/dashboard/appointments', icon: Calendar, label: 'Citas' },
  { path: '/dashboard/conversations', icon: MessageSquare, label: 'Conversaciones' },
  { path: '/dashboard/mass-messaging', icon: Send, label: 'WhatsApp Masivo' },
  { path: '/dashboard/bulk-email', icon: Mail, label: 'Email Masivo' },
  { path: '/dashboard/messenger-setup', icon: Instagram, label: 'Instagram/Facebook' },
  { path: '/dashboard/social-comments', icon: MessageSquare, label: 'Comentarios IA' },
  { path: '/dashboard/reviews', icon: Star, label: 'Reseñas' },
  { path: '/dashboard/meta-ads', icon: Facebook, label: 'Meta Ads' },
  { path: '/dashboard/meta-capi', icon: Activity, label: 'Conversions API' },
  { path: '/dashboard/tiktok-ads', icon: Music, label: 'TikTok Ads' },
  { path: '/dashboard/google-ads', icon: Search, label: 'Google Ads' },
  { path: '/dashboard/team', icon: UserCog, label: 'Equipo' },
  { path: '/dashboard/integrations', icon: Plug, label: 'Integraciones' },
  { path: '/dashboard/whatsapp-setup', icon: Settings, label: 'Configurar WhatsApp' },
  { path: '/dashboard/subscription', icon: CreditCard, label: 'Suscripción' },
];

const DashboardLayout = ({ children, title }) => {
  const location = useLocation();
  const assistant = useAssistant();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clinic, setClinic] = useState(null);
  const { theme } = useTheme();

  useEffect(() => {
    // Verify session is valid
    const clinicId = localStorage.getItem('clinicId');
    const token = localStorage.getItem('token');
    
    if (!clinicId || !token) {
      console.log('❌ No session found, redirecting to login');
      navigate('/login');
      return;
    }
    
    // Only verify token once per component mount, not on every navigation
    // Skip if we already have clinic data
    if (clinic) {
      return;
    }
    
    // Verify token is still valid by fetching clinic
    fetch(`${API}/clinic/${clinicId}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => {
        if (res.status === 401 || res.status === 403) {
          console.log('❌ Token expired or invalid, clearing session');
          localStorage.removeItem('token');
          localStorage.removeItem('clinicId');
          navigate('/login');
          return null;
        }
        return res.json();
      })
      .then(data => {
        if (data) {
          setClinic(data);
        }
      })
      .catch(err => {
        console.error('Error verifying session:', err);
        // Don't logout on network errors, only on auth errors
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Remove navigate dependency to prevent re-running on every navigation

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('clinicId');
    navigate('/login');
  };

  // Theme classes
  const bgMain = theme === 'dark' ? 'bg-[#0a0a0f]' : 'bg-gray-50';
  const bgSidebar = theme === 'dark' ? 'bg-[#0d0d12]' : 'bg-white';
  const borderColor = theme === 'dark' ? 'border-white/5' : 'border-gray-200';
  const textPrimary = theme === 'dark' ? 'text-white' : 'text-gray-900';
  const textSecondary = theme === 'dark' ? 'text-gray-400' : 'text-gray-600';
  const textMuted = theme === 'dark' ? 'text-gray-500' : 'text-gray-500';
  const hoverBg = theme === 'dark' ? 'hover:bg-white/5' : 'hover:bg-gray-100';
  const activeBg = theme === 'dark' ? 'bg-gradient-to-r from-blue-600/20 to-cyan-600/20 border-l-2 border-blue-500' : 'bg-blue-50 border-l-2 border-blue-500';

  return (
    <div className={`min-h-screen ${bgMain}`}>
      {/* Mobile Header */}
      <header className={`lg:hidden fixed top-0 left-0 right-0 z-50 ${bgMain}/95 backdrop-blur-xl border-b ${borderColor} h-16 flex items-center justify-between px-4`}>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">AI</span>
          </div>
          <span className={`${textPrimary} font-semibold`}>Med Planner</span>
        </div>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button onClick={() => setSidebarOpen(!sidebarOpen)} className={`${textPrimary} p-2`}>
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </header>

      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 z-40 h-full w-64 ${bgSidebar} border-r ${borderColor} transform transition-transform duration-300 lg:translate-x-0 flex flex-col ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Logo */}
        <div className={`h-16 flex items-center justify-between px-4 border-b ${borderColor} flex-shrink-0`}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">AI</span>
            </div>
            <span className={`${textPrimary} font-semibold`}>Med Planner</span>
          </div>
          <ThemeToggle className="hidden lg:block" />
        </div>

        {/* Clinic Info */}
        {clinic && (
          <div className={`p-4 border-b ${borderColor} flex-shrink-0`}>
            <p className={`${textPrimary} font-medium text-sm truncate`}>{clinic.name}</p>
            <p className={`${textMuted} text-xs truncate`}>{clinic.specialty}</p>
          </div>
        )}

        {/* Menu - Scrollable */}
        <nav className="p-3 space-y-0.5 overflow-y-auto flex-1">
          {assistant && <button type="button" onClick={() => { assistant.openAssistant(); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg ${textSecondary} ${hoverBg}`}>
            <LifeBuoy className="w-4 h-4" /><span className="text-sm font-medium">Asistente y ayuda</span>
          </button>}
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive
                    ? activeBg + ' text-blue-500'
                    : `${textSecondary} ${hoverBg} hover:${textPrimary}`
                }`}
              >
                <item.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{item.label}</span>
                {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
              </Link>
            );
          })}
        </nav>

        {/* Logout - Fixed at bottom */}
        <div className={`p-4 border-t ${borderColor} flex-shrink-0`}>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className={`w-full justify-start ${textSecondary} hover:${textPrimary} ${hoverBg}`}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Cerrar sesión
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`lg:ml-64 pt-16 lg:pt-0 min-h-screen ${bgMain}`}>
        {/* Page Header */}
        <div className={`border-b ${borderColor} ${bgMain}/80 backdrop-blur-xl sticky top-16 lg:top-0 z-30`}>
          <div className="px-6 py-4">
            <h1 className={`text-xl font-semibold ${textPrimary}`}>{title}</h1>
          </div>
        </div>

        {/* Page Content */}
        <div className="p-6">
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="lg:hidden fixed inset-0 z-30 bg-black/50"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;

